import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Close, ChevronDown } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core/i18n';
import type { TagsInputProps, TagOption } from './TagsInput.types';
import {
  Container,
  InputContainer,
  Tag,
  TagLabel,
  RemoveButton,
  InputWrapper,
  StyledInput,
  ChevronWrapper,
  Dropdown,
  DropdownList,
  OptionButton,
  CreateOption,
  SuggestionHint,
} from './TagsInput.styles';

export const TagsInput: React.FC<TagsInputProps> = ({
  options,
  value = [],
  onChange,
  onCreateTag,
  isCreating = false,
  placeholder,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const { tq } = useTranslation('tagsAdmin');
  const defaultPlaceholder = placeholder ?? tq`Add a label...`;
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [inputValue]);

  // Scroll highlighted option into view
  useEffect(() => {
    const dropdown = optionsRef.current;
    if (dropdown && isFocused) {
      const highlightedOption = dropdown.querySelector(`[data-highlighted="true"]`) as HTMLElement;
      if (highlightedOption) {
        highlightedOption.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isFocused]);

  const handleContainerClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setIsFocused(false);
      setInputValue('');
    }, 150);
  }, []);

  // Memoize selected IDs as a Set for O(1) lookup
  const selectedIds = useMemo(() => new Set(value.map(v => v.id)), [value]);

  // Memoize available options (options not already selected)
  const availableOptions = useMemo(() => options.filter(option => !selectedIds.has(option.id)), [options, selectedIds]);

  // Memoize filtered options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue) return availableOptions;
    const searchTerm = inputValue.toLowerCase();
    return availableOptions.filter(option => option.label.toLowerCase().includes(searchTerm));
  }, [availableOptions, inputValue]);

  // Find case-insensitive match (for showing warning when casing differs)
  const caseInsensitiveMatch = useMemo(() => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return null;

    const searchTerm = trimmedInput.toLowerCase();
    return [...value, ...availableOptions].find(o => o.label.toLowerCase() === searchTerm) ?? null;
  }, [inputValue, value, availableOptions]);

  // Check if the match is already assigned (in value array)
  const isMatchAlreadyAssigned = useMemo(() => {
    if (!caseInsensitiveMatch) return false;
    return selectedIds.has(caseInsensitiveMatch.id);
  }, [caseInsensitiveMatch, selectedIds]);

  // Can create if: input exists, callback exists, and NO case-insensitive match
  const canCreateTag = useMemo(() => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || !onCreateTag) return false;
    return !caseInsensitiveMatch;
  }, [inputValue, onCreateTag, caseInsensitiveMatch]);

  const handleAdd = useCallback(
    (option: TagOption) => {
      if (disabled || isCreating || (option.id !== '' && selectedIds.has(option.id))) return;

      const newValue = [...value, option];
      onChange?.(newValue);
      setInputValue('');

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    [disabled, isCreating, selectedIds, value, onChange],
  );

  const handleCreate = useCallback(async () => {
    if (!onCreateTag || isCreating || disabled) return;

    const trimmedLabel = inputValue.trim();
    if (!trimmedLabel) return;

    const newTag = await onCreateTag(trimmedLabel);
    if (newTag) {
      setInputValue('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [onCreateTag, isCreating, disabled, inputValue]);

  const getSelectableOptions = useCallback(() => {
    const selectableOptions: Array<{ type: 'option' | 'create'; value: TagOption }> = filteredOptions.map(option => ({
      type: 'option' as const,
      value: option,
    }));
    if (canCreateTag) {
      selectableOptions.push({ type: 'create', value: { id: '', label: inputValue.trim() } });
    }
    return selectableOptions;
  }, [filteredOptions, canCreateTag, inputValue]);

  const handleRemove = useCallback(
    (tagId: string) => {
      if (disabled) return;
      const newValue = value.filter(v => v.id !== tagId);
      onChange?.(newValue);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    [disabled, value, onChange],
  );

  // Show hint when match is already assigned (regardless of casing)
  const showAlreadyAssignedHint = caseInsensitiveMatch && isMatchAlreadyAssigned;

  const showDropdown = isFocused && (filteredOptions.length > 0 || canCreateTag || showAlreadyAssignedHint);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const selectableOptions = getSelectableOptions();

      switch (e.key) {
        case 'Escape':
          inputRef.current?.blur();
          break;

        case 'Backspace':
          if (inputValue === '' && value.length > 0) {
            const newValue = [...value];
            newValue.pop();
            onChange?.(newValue);
          }
          break;

        case 'ArrowDown':
          if (showDropdown) {
            e.preventDefault();
            setHighlightedIndex(prev => (prev < selectableOptions.length - 1 ? prev + 1 : 0));
          }
          break;

        case 'ArrowUp':
          if (showDropdown) {
            e.preventDefault();
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : selectableOptions.length - 1));
          }
          break;

        case 'Enter':
          if (showDropdown && selectableOptions.length > 0) {
            e.preventDefault();
            const selected = selectableOptions[highlightedIndex];
            if (selected) {
              if (selected.type === 'create') {
                handleCreate();
              } else {
                handleAdd(selected.value);
              }
            }
          }
          break;
      }
    },
    [getSelectableOptions, inputValue, value, onChange, highlightedIndex, handleAdd, handleCreate, showDropdown],
  );

  return (
    <Container data-testid={dataTestId}>
      <InputContainer
        ref={containerRef}
        onClick={handleContainerClick}
        $isFocused={isFocused}
        $isDisabled={disabled}
      >
        {value.map(tag => (
          <Tag key={tag.id}>
            <TagLabel>{tag.label}</TagLabel>
            <RemoveButton
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                handleRemove(tag.id);
              }}
              disabled={disabled}
              aria-label={tq`Remove ${tag.label}`}
            >
              <Close size={12} />
            </RemoveButton>
          </Tag>
        ))}
        <InputWrapper>
          <StyledInput
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={value.length === 0 ? defaultPlaceholder : ''}
            autoComplete="off"
          />
          <ChevronWrapper $isOpen={isFocused}>
            <ChevronDown size={14} />
          </ChevronWrapper>
        </InputWrapper>
      </InputContainer>
      {showDropdown && (
        <Dropdown
          role="listbox"
          id="tags-listbox"
        >
          {caseInsensitiveMatch && (isMatchAlreadyAssigned || caseInsensitiveMatch.label !== inputValue.trim()) && (
            <SuggestionHint>
              {isMatchAlreadyAssigned
                ? tq`"${caseInsensitiveMatch.label}" is already assigned. Tags ignore capitalization.`
                : tq`"${caseInsensitiveMatch.label}" already exists. Tags ignore capitalization. Select it below or enter a different name.`}
            </SuggestionHint>
          )}
          <DropdownList ref={optionsRef}>
            {filteredOptions.map((option, index) => {
              const isHighlighted = highlightedIndex === index;
              return (
                <OptionButton
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={isHighlighted}
                  data-highlighted={isHighlighted}
                  $isHighlighted={isHighlighted}
                  onMouseDown={e => {
                    e.preventDefault();
                    handleAdd(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {option.label}
                </OptionButton>
              );
            })}
            {canCreateTag && (
              <CreateOption
                type="button"
                role="option"
                aria-selected={highlightedIndex === filteredOptions.length}
                data-highlighted={highlightedIndex === filteredOptions.length}
                $isHighlighted={highlightedIndex === filteredOptions.length}
                onMouseDown={e => {
                  e.preventDefault();
                  handleCreate();
                }}
                onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                disabled={isCreating}
              >
                {isCreating ? tq`Creating...` : tq`+ Create "${inputValue.trim()}"`}
              </CreateOption>
            )}
          </DropdownList>
        </Dropdown>
      )}
    </Container>
  );
};
